import React from 'react';
import styled from 'styled-components';
import { StaticImage } from 'gatsby-plugin-image';
import { map } from 'styled-components-breakpoint';
import { p, pt, px, py, pb, mb, mt, my } from 'styled-components-spacing';

import { colors, Text, Heading } from 'themes/components';
import HighlightText from 'utils/HighlightText';
import GlowBorder from 'components/GlowBorder';
import pipelineBg from './images/pipeline-bg.png';
import IntegrateIcon from './icons/IntegrateIcon';
import AutomateIcon from './icons/AutomateIcon';
import TemplateIcon from './icons/TemplateIcon';

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  ${mt({ mobile: 4, tablet: 8, desktop: 12 })};
  ${pt({ mobile: 6, tablet: 8 })};
`;

const TopContent = styled.div`
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
`;

const Tag = styled.span`
  color: ${colors.grey30};
  font-size: 16px;
  font-weight: 500;
  padding: 8px 12px;
  background: linear-gradient(
    to top left,
    rgba(84, 212, 114, 0.15),
    rgba(84, 212, 255, 0.15)
  );
  border-radius: 6px;
  border: 1px solid ${colors.teal40}15;
`;

const Title = styled(Heading)`
  color: ${colors.grey5};
  ${my(2)};
`;

const Description = styled(Text)`
  color: ${colors.grey30};
  ${mt(1)};
`;

const PipelineFeatures = styled.div`
  background: url(${pipelineBg}) no-repeat center;
  background-size: contain;
  ${py({ mobile: 2, tablet: 4 })};
  ${mt({ mobile: 2, tablet: 4 })};
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`;

const Feature = styled.div`
  ${px(3)};
  ${py(2)};
  color: ${colors.grey5};
  border-radius: 16px;
  border: 1px solid ${colors.grey70};
  background: rgba(18, 18, 22, 0.5);
  backdrop-filter: blur(0.3px);
  align-self: start;
  ${map(
    {
      mobile: '0 0 100%',
      desktop: '0 0 30%'
    },
    (flex) => `flex: ${flex};`
  )};
  &:nth-of-type(2) {
    ${map(
      {
        desktop: '-24px'
      },
      (mt) => `margin-top: ${mt};`
    )};
  }
`;

const FeatureTitle = styled(Heading)`
  color: ${colors.grey5};
  ${mb(1)};
  ${mt(2)};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FeatureDescription = styled(Text)`
  color: ${colors.grey30};
`;

const PipelineAutomation = () => (
  <Container>
    <GlowBorder
      borderColor="hsla(140, 48%, 53%, 0.3)"
      glow1="hsla(197, 100%, 75%, 0.38)"
      glow2="hsla(140, 68%, 52%, 0.4)"
      glow3="hsla(302, 95%, 85%, 0.4)"
    />
    <TopContent>
      <Tag>
        <HighlightText
          direction="top right"
          stopOne={colors.teal40}
          stopTwo={colors.blue40}
        >
          In development
        </HighlightText>
      </Tag>
      <Title variant="h2">Pipeline automation & management</Title>
      <Description>
        Studios have to do more with less these days. Mudstack isn't just a
        solution for today—we're building to help you grow your studio more
        efficiently.
      </Description>
      <Description>
        Define your pipelines in Mudstack, and let us shepherd your content from
        concept to completion and beyond.
      </Description>
    </TopContent>

    <PipelineFeatures>
      <Feature>
        <StaticImage src="./images/template.png" alt="Template" />
        <FeatureTitle>
          <TemplateIcon fillColor={colors.purple30} />
          Template
        </FeatureTitle>
        <FeatureDescription>
          Create templates for your studios pipelines, so art teams have
          guardrails and a repeatable workflow each time.
        </FeatureDescription>
      </Feature>
      <Feature>
        <StaticImage src="./images/automate.png" alt="Automate" />
        <FeatureTitle>
          <AutomateIcon fillColor={colors.orange40} />
          Automate
        </FeatureTitle>
        <FeatureDescription>
          Take away the tedious tasks that no one wants to do. Mudstack helps
          you automate repetitive workflows.
        </FeatureDescription>
      </Feature>
      <Feature>
        <StaticImage src="./images/integrate.png" alt="Integrate" />
        <FeatureTitle>
          <IntegrateIcon fillColor={colors.teal40} />
          Integrate
        </FeatureTitle>
        <FeatureDescription>
          Send content & metadata anywhere, whether it's for game builds,
          project management, or something else.
        </FeatureDescription>
      </Feature>
    </PipelineFeatures>
  </Container>
);

export default PipelineAutomation;
